import React from 'react';
import {
  ArrowRight, Check, Sparkles, Shield, Zap, BarChart3, Award,
  Boxes, Headphones, Cloud, Lock, HardDrive, MessageSquare, Settings2,
  Phone, Calendar, Layers, BadgeCheck, ShieldCheck,
} from 'lucide-react';
import SystemsHeader from '../components/SystemsHeader';
import DivisionStripBar from '../components/DivisionStripBar';
import SystemsFooter from '../components/SystemsFooter';
import AppearancePreFooter from '../components/AppearancePreFooter';
import SeoHead from '../components/SeoHead';
import StructuredData from '../components/StructuredData';
import FAQSection from '../components/FAQSection';
import SystemsPartnersStrip from '../components/systems/SystemsPartnersStrip';
import CrossDivisionStrip from '../components/CrossDivisionStrip';
import RecommendedForYou from '../components/RecommendedForYou';
import { useI18n } from '../context/I18nContext';
import {
  SYSTEMS_HERO, SYSTEMS_STATS, SYSTEMS_WHY, SYSTEMS_SOLUTIONS,
  SYSTEMS_PROCESS, SYSTEMS_EXPERTISE, SYSTEMS_COMPARE, SYSTEMS_FINAL_CTA,
} from '../data/systemsData';

// ===== Brand SVG logos =====
const Microsoft365Logo = () => (
  <svg viewBox="0 0 24 24" className="h-10 w-10 text-[#F25022]" stroke="currentColor" fill="none" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 18h9v-12l-5 2v5l-4 2v-8l9 -4l7 2v13l-7 3z" />
  </svg>
);
const AWSLogo = () => (
  <svg viewBox="0 0 24 24" className="h-10 w-10 text-[#FF9900]" fill="currentColor">
    <path d="M6.763 10.036c0 .296.032.535.088.71.064.176.144.368.256.576.04.063.056.127.056.183 0 .08-.048.16-.152.24l-.503.335a.383.383 0 0 1-.208.072c-.08 0-.16-.04-.239-.112a2.47 2.47 0 0 1-.287-.375 6.18 6.18 0 0 1-.248-.471c-.622.734-1.405 1.101-2.347 1.101-.67 0-1.205-.191-1.596-.574-.391-.384-.59-.894-.59-1.533 0-.678.239-1.23.726-1.644.487-.415 1.133-.623 1.955-.623.272 0 .551.024.846.064.296.04.6.104.918.176v-.583c0-.607-.127-1.03-.375-1.277-.255-.248-.686-.367-1.3-.367-.28 0-.568.031-.863.103-.295.072-.583.16-.862.272a2.287 2.287 0 0 1-.28.104.488.488 0 0 1-.127.023c-.112 0-.168-.08-.168-.247v-.391c0-.128.016-.224.056-.28a.597.597 0 0 1 .224-.167c.279-.144.614-.264 1.005-.36a4.84 4.84 0 0 1 1.246-.151c.95 0 1.644.216 2.091.647.439.43.662 1.085.662 1.963v2.586zm-3.24 1.214c.263 0 .534-.048.822-.144.287-.096.543-.271.758-.51.128-.152.224-.32.272-.512.047-.191.08-.423.08-.694v-.335a6.66 6.66 0 0 0-.735-.136 6.02 6.02 0 0 0-.75-.048c-.535 0-.926.104-1.19.32-.263.215-.39.518-.39.917 0 .375.095.655.295.846.191.2.47.296.838.296zM21.698 16.207c-2.626 1.94-6.442 2.969-9.722 2.969-4.598 0-8.74-1.7-11.87-4.526-.247-.223-.024-.527.272-.351 3.384 1.963 7.559 3.153 11.877 3.153 2.914 0 6.114-.607 9.06-1.852.439-.2.814.287.383.607zM22.792 14.961c-.336-.43-2.22-.207-3.074-.103-.255.032-.295-.192-.063-.36 1.5-1.053 3.967-.75 4.254-.399.287.36-.08 2.826-1.485 4.007-.215.184-.423.088-.327-.151.32-.79 1.03-2.57.695-2.994z"/>
  </svg>
);
const GoogleCloudLogo = () => (
  <svg viewBox="0 0 24 24" className="h-10 w-10 text-[#4285F4]" fill="currentColor">
    <path d="M12.19 2.38a9.344 9.344 0 0 0-9.234 6.893c.053-.02-.055.013 0 0-3.875 2.551-3.922 8.11-.247 10.941l.006-.007-.007.03a6.717 6.717 0 0 0 4.077 1.356h5.173l.03.03h5.192c6.687.053 9.376-8.605 3.835-12.35a9.365 9.365 0 0 0-2.821-4.552l-.043.043.006-.05A9.344 9.344 0 0 0 12.19 2.38zm-.358 4.146c1.244-.04 2.518.368 3.486 1.15a5.186 5.186 0 0 1 1.862 4.078v.518c3.53-.07 3.53 5.262 0 5.193h-5.193l-.008.009v-.04H6.785a2.59 2.59 0 0 1-1.067-.23h.001a2.597 2.597 0 1 1 3.437-3.437l3.013-3.012A6.747 6.747 0 0 0 8.11 8.24c.018-.01.04-.026.054-.023a5.186 5.186 0 0 1 3.67-1.69z"/>
  </svg>
);
const CiscoLogo = () => (
  <svg viewBox="0 0 24 24" className="h-10 w-10 text-[#049FD9]" fill="currentColor">
    <path d="M16.331 18.171V17.06l-.022.01c-.25.121-.522.19-.801.203a1.186 1.186 0 01-.806-.237 1.038 1.038 0 01-.352-.498 1.21 1.21 0 01-.023-.667c.052-.225.178-.426.357-.569.16-.134.355-.218.562-.242a1.85 1.85 0 011.061.198l.024.013v-1.117l-.051-.014a2.862 2.862 0 00-1.011-.132 2.34 2.34 0 00-.903.206c-.287.132-.54.327-.739.571a2.221 2.221 0 00-.04 2.705c.295.378.709.645 1.175.756.491.12 1.006.102 1.487-.052l.082-.023M11.667 18.244h1.05v-4.167h-1.05v4.167M22.961 10.728a.52.52 0 001.039 0V9.573a.52.52 0 00-1.039 0v1.155M20.117 10.728a.522.522 0 001.041 0V8.139a.521.521 0 00-1.04 0v2.589M17.231 11.771a.521.521 0 001.039 0V6.17a.52.52 0 00-1.039 0v5.601M14.393 10.728a.521.521 0 001.04 0V8.139a.52.52 0 00-1.039 0v2.589M11.494 10.728a.522.522 0 001.039 0V9.573a.52.52 0 00-1.039 0v1.155M8.624 10.728a.52.52 0 001.039 0V8.139a.52.52 0 00-1.039 0v2.589M5.737 11.771a.52.52 0 001.039 0V6.17a.52.52 0 00-1.039 0v5.601M2.876 10.728a.522.522 0 001.04 0V8.139a.52.52 0 00-1.039 0v2.589M0 10.728a.521.521 0 001.039 0V9.573a.52.52 0 00-1.039 0v1.155"/>
  </svg>
);
const VMwareLogo = () => (
  <svg viewBox="0 0 24 24" className="h-10 w-10 text-[#607078]" fill="currentColor">
    <path d="M.5 10.1a.505.505 0 00-.197.048.497.497 0 00-.25.68l1.138 2.475c.179.38.38.592.721.592.342 0 .542-.22.72-.592l1.003-2.186a.144.144 0 01.144-.092.16.16 0 01.157.16v2.118a.535.535 0 101.066 0v-1.73a.531.531 0 01.566-.552.52.52 0 01.541.551v1.73a.531.531 0 00.53.593.539.539 0 00.535-.592v-1.73a.531.531 0 01.564-.552.52.52 0 01.543.551v1.73a.531.531 0 00.528.593.535.535 0 00.535-.592v-1.969a1.234 1.234 0 00-1.283-1.23 1.647 1.647 0 00-1.14.486 1.26 1.26 0 00-1.095-.483 1.807 1.807 0 00-1.074.483 1.287 1.287 0 00-.961-.483 1.177 1.177 0 00-1.158.786l-.729 1.716-.933-2.203.011-.004A.505.505 0 00.5 10.1zm18.43.06a.27.27 0 00-.266.274h.002v3.142a.27.27 0 10.535 0v-1.222c0-1.037.571-1.56 1.27-1.643a.266.266 0 00.238-.274.258.258 0 00-.266-.269 1.465 1.465 0 00-1.242.88v-.614a.266.266 0 00-.271-.274zm-6.735.008a.273.273 0 00-.25.217l-.912 2.627-.902-2.62a.28.28 0 00-.274-.22.266.266 0 00-.27.258.493.493 0 00.034.144l1.09 3.037.02-.007a.319.319 0 00.298.242.3.3 0 00.293-.242l.903-2.583.896 2.583a.3.3 0 00.293.242h.018a.319.319 0 00.293-.242l1.097-3.038a.512.512 0 00.033-.144.258.258 0 00-.265-.25.262.262 0 00-.258.209l-.918 2.63-.904-2.626a.285.285 0 00-.278-.217h-.025a.273.273 0 00-.012 0zm10.168.008a1.75 1.75 0 00-1.691 1.851 1.765 1.765 0 001.76 1.858l-.008.013a1.784 1.784 0 001.33-.539.228.228 0 00.082-.17.228.228 0 00-.379-.168 1.435 1.435 0 01-1.018.415 1.237 1.237 0 01-1.24-1.207h2.555a.247.247 0 00.246-.247c0-.945-.593-1.806-1.637-1.806zm-5.744.002a1.571 1.571 0 00-.158.006 2.384 2.384 0 00-1.078.205.22.22 0 00-.143.222.24.24 0 00.235.229.266.266 0 00.095-.024 1.822 1.822 0 01.834-.162c.691 0 1.07.334 1.07.979v.125a3.796 3.796 0 00-1.103-.15c-.892 0-1.52.4-1.52 1.16l-.003-.004c0 .736.671 1.117 1.34 1.117a1.575 1.575 0 001.298-.62v.343a.247.247 0 00.254.25.254.254 0 00.258-.262v-1.983a1.416 1.416 0 00-.379-1.046 1.571 1.571 0 00-1-.385zm5.719.43c.714 0 1.085.565 1.139 1.214h-2.278a1.222 1.222 0 011.139-1.215z"/>
  </svg>
);
const DellLogo = () => (
  <svg viewBox="0 0 24 24" className="h-10 w-10 text-[#007DB8]" fill="currentColor">
    <path d="M17.963 14.6V9.324h1.222v4.204h2.14v1.07h-3.362zM11.82 0h.337a11.94 11.94 0 0 1 5.405 1.373 12.101 12.101 0 0 1 4.126 3.557A11.93 11.93 0 0 1 24 11.82v.36a11.963 11.963 0 0 1-3.236 8.033A11.967 11.967 0 0 1 12.182 24h-.361a11.993 11.993 0 0 1-4.145-.806 12.04 12.04 0 0 1-4.274-2.836A12.057 12.057 0 0 1 .576 15.67 12.006 12.006 0 0 1 0 12.181v-.361a11.924 11.924 0 0 1 1.992-6.396 12.211 12.211 0 0 1 4.71-4.172A11.875 11.875 0 0 1 11.82 0m-.153 1.23a10.724 10.724 0 0 0-6.43 2.375 10.78 10.78 0 0 0-3.319 4.573 10.858 10.858 0 0 0 .193 8.12 10.788 10.788 0 0 0 3.546 4.421 10.698 10.698 0 0 0 4.786 1.946c1.456.209 2.955.124 4.376-.26a10.756 10.756 0 0 0 5.075-3.062 10.742 10.742 0 0 0 2.686-5.28 10.915 10.915 0 0 0-.122-4.682 10.77 10.77 0 0 0-7.098-7.626 10.78 10.78 0 0 0-3.693-.525z"/>
  </svg>
);
const BRAND_LOGOS = { 'Microsoft 365': Microsoft365Logo, 'AWS': AWSLogo, 'Google Cloud': GoogleCloudLogo, 'Cisco': CiscoLogo, 'VMware': VMwareLogo, 'Dell EMC': DellLogo };

// ===== HERO =====
function Hero() {
  const d = SYSTEMS_HERO;
  const { t } = useI18n();
  const ts = t.systems;
  return (
    <section className="relative overflow-hidden bg-[#FAFAFA] px-4 pb-14 md:px-8 md:pb-16 md:pt-5">
      <div className="pointer-events-none absolute right-0 top-10 h-[min(70vw,450px)] w-[min(70vw,450px)] rounded-full bg-gradient-to-br from-zinc-200 to-zinc-200 blur-3xl opacity-70 sm:top-20" aria-hidden="true" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-[min(65vw,500px)] w-[min(65vw,500px)] rounded-full bg-gradient-to-tr from-zinc-200 to-zinc-200 blur-3xl opacity-60" aria-hidden="true" />

      <div className="relative z-10 mx-auto max-w-4xl text-center pt-10 md:pt-14">
        <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-white px-5 py-2 backdrop-blur-sm">
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-[#C9A227] animate-pulse" />
          <span className="text-zinc-900 text-xs font-bold uppercase tracking-[0.22em] text-transparent">
            {ts.hero_eyebrow}
          </span>
        </div>

        <h1 className="mb-5 text-4xl font-bold leading-[1.08] tracking-tight text-slate-900 md:text-5xl lg:text-6xl">
          <span className="block">{ts.hero_title_a}</span>
          <span className="block text-zinc-900">
            {ts.hero_title_b}
          </span>
        </h1>
        <p className="mx-auto mb-8 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
          {ts.hero_body}
        </p>

        <div className="mb-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href={d.primaryCTA.href}
            data-testid="systems-hero-primary-cta"
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-3.5 text-base font-semibold text-[#0b0f19] shadow-lg transition-all hover:gap-3 hover:scale-[1.02] sm:w-auto"
            style={{ background: 'linear-gradient(90deg, #e6c35a 0%, #c9a227 50%, #a8871a 100%)' }}
          >
            <span>{ts.hero_primary_cta}</span>
            <ArrowRight className="h-5 w-5 shrink-0 transition-transform group-hover:translate-x-0.5" />
          </a>
          <a
            href="#final-cta"
            data-testid="systems-hero-secondary-cta"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-8 py-3.5 text-base font-semibold text-slate-800 shadow-sm transition hover:border-[#C9A227] sm:w-auto"
          >
            <Phone className="h-4 w-4 text-[#C9A227]" />
            <span>Speak with a strategist</span>
          </a>
        </div>

        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-4 lg:grid-cols-4">
          {SYSTEMS_STATS.map((s, i) => {
            const icons = [Calendar, BadgeCheck, Layers, ShieldCheck];
            const Icon = icons[i] || BadgeCheck;
            const translatedLabel = ts?.cards?.stats_labels?.[i] || s.label;
            return (
              <div key={s.label} className="rounded-2xl border-[0.5px] border-transparent bg-white p-4 text-left shadow-[0_12px_24px_rgba(0,0,0,0.08)] transition-all hover:-translate-y-1 hover:border-[#D4AF37] md:p-6">
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#fff8e7] text-[#c9a227]">
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </div>
                <p className="text-xl font-bold text-slate-900 md:text-2xl">{s.value}</p>
                <p className="text-sm text-slate-500">{translatedLabel}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ===== WHY =====
function Why() {
  const d = SYSTEMS_WHY;
  const { t } = useI18n();
  const ts = t.systems;
  const grads = [
    { icon: Shield, grad: 'from-blue-500 to-cyan-500' },
    { icon: Zap, grad: 'from-amber-500 to-orange-500' },
    { icon: BarChart3, grad: 'from-emerald-500 to-green-500' },
    { icon: Award, grad: 'from-purple-500 to-pink-500' },
  ];
  return (
    <section id="why" className="relative overflow-hidden bg-white py-16 md:py-20">
      <div className="relative mx-auto max-w-5xl px-4 text-center">
        <div className="mb-8 inline-flex items-center justify-center rounded-full border border-zinc-200 bg-[#FEFBF3] px-5 py-2.5">
          <span className="font-bold uppercase tracking-[0.14em] text-[#C9A227] text-sm">{ts.why_eyebrow}</span>
        </div>
        <h2 className="mb-4 text-3xl font-bold tracking-tight text-[#1D1D1F] md:text-4xl lg:text-[2.75rem] lg:leading-tight">{ts.why_title}</h2>
        <p className="mx-auto mb-12 max-w-2xl text-base leading-relaxed text-[#6E6E73] md:text-lg">{ts.why_subtitle}</p>

        <div className="grid grid-cols-1 gap-6 text-left md:grid-cols-2">
          {d.items.map((it, i) => {
            const tr = ts?.cards?.why_items?.[i] || {};
            const { icon: Icon, grad } = grads[i];
            return (
              <div key={it.title} className="group relative overflow-hidden rounded-3xl border border-[#E5E5E7] bg-white p-8 transition-all duration-500 hover:-translate-y-2 hover:scale-[1.02] hover:border-transparent hover:shadow-2xl hover:shadow-black/10">
                <div className="relative z-10">
                  <div className={`mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${grad} shadow-md transition-all duration-500 group-hover:scale-110 group-hover:rotate-3`}>
                    <Icon className="h-7 w-7 text-white" strokeWidth={2} />
                  </div>
                  <h3 className="mb-3 text-2xl font-bold text-[#1D1D1F]">{tr.title || it.title}</h3>
                  <p className="leading-relaxed text-[#6E6E73]">{tr.body || it.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ===== SOLUTIONS =====
function Solutions() {
  const d = SYSTEMS_SOLUTIONS;
  const { t } = useI18n();
  const ts = t.systems;
  const translated = ts?.cards?.solutions_items || [];
  // Merge English layout (featured flag, icon) with translated text
  const mergedItems = d.items.map((it, idx) => ({ ...it, ...(translated[idx] || {}) }));
  const featured = mergedItems.find((x) => x.featured);
  const rest = mergedItems.filter((x) => !x.featured);
  const iconMap = { Cpu: Boxes, Headphones, Cloud, Lock, HardDrive };
  return (
    <section id="solutions" className="relative overflow-hidden bg-gradient-to-b from-[#F5F5F7] to-white px-4 py-16 md:py-20 sm:px-8 lg:px-16">
      <div className="relative z-10 mx-auto max-w-[1200px]">
        <div className="mb-10 md:mb-12 text-center">
          <h2 className="mb-4 text-4xl font-bold text-[#1D1D1F] sm:text-5xl">{ts.solutions_title}</h2>
          <p className="mx-auto max-w-2xl text-xl text-[#6E6E73]">{ts.solutions_subtitle}</p>
        </div>

        {/* Featured dark card */}
        {featured && (
          <a href="/systems/solutions/managed-it-services" className="group relative mb-6 block overflow-hidden rounded-3xl bg-white border border-zinc-200 p-10 md:p-12 transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl">
            <div className="absolute top-0 right-0 h-64 w-64 rounded-full bg-[#C9A227] opacity-10 blur-3xl transition-all duration-700 group-hover:scale-110 group-hover:opacity-20" />
            <div className="absolute right-32 bottom-0 h-48 w-48 rounded-full bg-[#E6C35A] opacity-5 blur-2xl" />
            <div className="relative z-10 max-w-2xl">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-[#C9A227] px-3 py-1 text-xs font-bold text-white">
                <Sparkles className="h-3 w-3 animate-pulse" />
                <span>{featured.badge}</span>
              </div>
              <h3 className="mb-4 text-3xl font-bold text-zinc-900 sm:text-4xl">{featured.title}</h3>
              <p className="mb-8 text-xl leading-relaxed text-zinc-600">{featured.body}</p>
              <div className="inline-flex items-center gap-2 font-semibold text-[#C9A227] group-hover:text-[#E6C35A] transition-all group-hover:gap-3">
                <span>{featured.cta}</span>
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </div>
            </div>
          </a>
        )}

        {/* Rest cards */}
        <div className="grid gap-6 px-1 py-2 sm:grid-cols-2 lg:grid-cols-3">
          {rest.map((it) => {
            const Icon = iconMap[it.icon] || Boxes;
            return (
              <a key={it.title} href="/systems/solutions" className="group relative block overflow-hidden rounded-2xl border border-[#E5E5E7] bg-white p-8 transition-all duration-500 hover:scale-[1.05] hover:border-[#C9A227] hover:shadow-2xl hover:shadow-[#C9A227]/20">
                <div className="relative">
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-xl bg-[#C9A227]/10 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 group-hover:bg-[#C9A227]">
                    <Icon className="h-7 w-7 text-[#C9A227] group-hover:text-white transition-colors" strokeWidth={2} />
                  </div>
                  <div className="mb-2 text-xs font-bold uppercase tracking-wide text-[#6E6E73]">{it.badge}</div>
                  <h3 className="mb-3 text-xl font-bold text-[#1D1D1F] group-hover:text-[#C9A227] transition-colors">{it.title}</h3>
                  <p className="mb-5 text-sm leading-relaxed text-[#6E6E73]">{it.body}</p>
                  <div className="inline-flex items-center gap-1 text-sm font-semibold text-[#C9A227] group-hover:gap-2 transition-all">
                    <span>{it.cta}</span>
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <a href="/systems/solutions" className="inline-flex items-center gap-2 rounded-full border border-[#1D1D1F] bg-transparent px-7 py-3 text-[15px] font-semibold text-[#1D1D1F] hover:bg-[#f5a623] hover:text-[#0e0e10] transition-colors">
            {ts.solutions_view_all}
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

// ===== PROCESS =====
function Process() {
  const d = SYSTEMS_PROCESS;
  const { t } = useI18n();
  const ts = t.systems;
  const icons = [MessageSquare, Settings2, Headphones];
  return (
    <section className="relative overflow-hidden bg-[#FAFAFA] py-16 md:py-20 px-4">
      <div className="relative z-10 mx-auto max-w-5xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#C9A227]/25 bg-[#FEFBF3] px-5 py-2 mb-6">
            <Sparkles className="h-3.5 w-3.5 text-[#C9A227]" />
            <span className="font-bold uppercase tracking-[0.14em] text-[#C9A227] text-xs">{ts.process_eyebrow}</span>
          </div>
          <h2 className="mb-4 text-3xl md:text-5xl font-bold text-[#1D1D1F] tracking-tight">{ts.process_title}</h2>
          <p className="mx-auto max-w-2xl text-lg text-[#6E6E73]">{ts.process_subtitle}</p>
        </div>

        <div className="relative">
          {/* Vertical timeline line */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-zinc-200" aria-hidden="true" />
          <div className="space-y-16">
            {d.steps.map((st, i) => {
              const tr = ts?.cards?.process_items?.[i] || {};
              const title = tr.title || st.title;
              const body = tr.body || st.body;
              const bullets = (tr.bullets && tr.bullets.length ? tr.bullets : st.bullets);
              const stepLabel = ts?.cards?.compare_step_label || 'STEP';
              const Icon = icons[i] || MessageSquare;
              const leftSide = i % 2 === 0;
              return (
                <div key={st.n} className="relative grid lg:grid-cols-2 gap-8 items-center">
                  {/* Dot */}
                  <div className="hidden lg:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-br from-[#e6c35a] to-[#c9a227] border-4 border-white shadow-lg" />
                  </div>

                  {/* Text side */}
                  <div className={`${leftSide ? 'lg:text-right lg:pr-16' : 'lg:col-start-2 lg:pl-16'}`}>
                    <div className={`inline-flex items-center gap-1.5 mb-4 rounded-full bg-zinc-100 border border-zinc-200 px-3 py-1 ${leftSide ? 'lg:ml-auto' : ''}`}>
                      <MessageSquare className="h-3 w-3 text-[#C9A227]" />
                      <span className="text-xs font-bold tracking-wide text-[#C9A227]">{stepLabel} {parseInt(st.n, 10)}</span>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-[#1D1D1F] mb-3">{title}</h3>
                    <p className={`text-[#6E6E73] leading-relaxed ${leftSide ? 'lg:ml-auto' : ''} max-w-md`}>{body}</p>
                  </div>

                  {/* Card side */}
                  <div className={`${leftSide ? 'lg:col-start-2 lg:pl-16' : 'lg:col-start-1 lg:row-start-1 lg:pr-16'}`}>
                    <div className="group relative overflow-hidden rounded-3xl border border-[#E5E5E7] bg-gradient-to-br from-white via-[#FAFAFA] to-white p-8 shadow-sm transition-all duration-500 hover:border-[#C9A227]/60 hover:shadow-2xl hover:shadow-[#C9A227]/15">
                      <div className="mx-auto mb-6 h-20 w-20 rounded-2xl bg-gradient-to-br from-[#e6c35a] to-[#c9a227] flex items-center justify-center shadow-lg">
                        <Icon className="h-10 w-10 text-white" strokeWidth={2} />
                      </div>
                      <ul className="space-y-3 max-w-md mx-auto">
                        {bullets.map((b) => (
                          <li key={b} className="flex items-center gap-3 text-base font-medium text-[#6E6E73] group-hover:text-[#1D1D1F] transition-colors">
                            <Check className="h-5 w-5 shrink-0 text-[#C9A227]" strokeWidth={2.5} />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ===== EXPERTISE =====
function Expertise() {
  const d = SYSTEMS_EXPERTISE;
  const { t } = useI18n();
  const ts = t.systems;
  return (
    <section id="expertise" className="py-16 md:py-20 px-4 md:px-8 bg-neutral-100">
      <div className="max-w-6xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-gray-900">{ts.expertise_title}</h2>
        <p className="text-base md:text-lg max-w-2xl mx-auto mb-10 md:mb-12 text-gray-600">{ts.expertise_subtitle}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 overflow-visible p-1 text-center">
          {d.platforms.map((p, i) => {
            const Logo = BRAND_LOGOS[p.name];
            const desc = ts?.cards?.expertise_descs?.[i] || p.desc;
            return (
              <div key={p.name} className="group flex flex-col items-center rounded-xl border-2 border-transparent px-6 py-8 shadow-md bg-white shadow-gray-200/90 transition-all duration-500 hover:scale-105 hover:border-amber-500 hover:shadow-xl">
                <div className="mb-4 flex h-14 w-14 shrink-0 items-center justify-center">
                  {Logo ? <Logo /> : <div className="w-10 h-10 rounded bg-gray-200" />}
                </div>
                <h3 className="mb-1 text-base font-bold md:text-lg text-gray-900">{p.name}</h3>
                <p className="text-sm text-gray-500">{desc}</p>
              </div>
            );
          })}
        </div>
        <p className="mt-8 md:mt-10 text-sm md:text-base text-gray-500">{ts?.cards?.expertise_footer || d.footer}</p>
      </div>
    </section>
  );
}

// ===== COMPARE =====
function Compare() {
  const d = SYSTEMS_COMPARE;
  const { t } = useI18n();
  const ts = t.systems;
  return (
    <section id="compare" className="py-16 md:py-20 px-4 sm:px-8 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-3 text-black">{ts.compare_title}</h2>
          <p className="text-base md:text-lg max-w-2xl mx-auto text-gray-500">{ts.compare_subtitle}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {d.columns.map((c, ci) => {
            const tr = ts?.cards?.compare_columns?.[ci] || {};
            const brand = tr.brand || c.brand;
            const tier = tr.tier || c.tier;
            const items = (tr.items && tr.items.length ? tr.items : c.items);
            return (
              <div
                key={c.brand}
                className={`rounded-2xl overflow-hidden flex flex-col ${c.highlight ? 'shadow-lg' : 'border border-gray-300 bg-white dark:bg-zinc-900 dark:border-zinc-700'}`}
                style={c.highlight ? { backgroundColor: '#D4AF37' } : undefined}
              >
                <div className={`px-6 pt-8 pb-6 text-center ${c.highlight ? 'text-white' : ''}`}>
                  <div className={`inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4 ${c.highlight ? 'bg-white/25 text-white border border-white/40' : 'bg-white text-gray-600 border border-gray-300 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700'}`}>
                    {brand}
                  </div>
                  <h3 className={`text-lg font-bold ${c.highlight ? 'text-white' : 'text-black dark:text-zinc-100'}`}>{tier}</h3>
                </div>
                <ul className="px-6 pb-8 space-y-3 flex-1">
                  {items.map((it) => (
                    <li key={it} className="flex gap-3 items-start text-left">
                      {c.positive ? (
                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-white/50 bg-white/15">
                          <Check className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
                        </span>
                      ) : (
                        <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-gray-400" aria-hidden="true" />
                      )}
                      <span className={`text-sm leading-snug ${c.highlight ? 'text-white' : 'text-gray-700 dark:text-zinc-300'}`}>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ===== FINAL CTA =====
function FinalCTA() {
  const d = SYSTEMS_FINAL_CTA;
  const { t } = useI18n();
  const ts = t.systems;
  return (
    <section id="final-cta" className="py-16 md:py-20 px-4 md:px-8 bg-[radial-gradient(ellipse_90%_70%_at_50%_20%,#ffffff_0%,#FDFCFB_45%,#f5f1ea_100%)]">
      <div className="max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-7 border bg-[#F5F0E8]/90 border-stone-200/70 shadow-sm">
          <span className="w-2 h-2 rounded-full bg-[#D4AF37] animate-pulse" aria-hidden="true" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#1a1a1a]">{ts.cta_eyebrow}</span>
        </div>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-5 leading-[1.2] max-w-2xl mx-auto text-[#1a1a1a]">{ts.cta_title}</h2>
        <p className="text-base md:text-lg max-w-2xl mx-auto mb-9 leading-relaxed text-[#666]">{ts.cta_body}</p>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 mb-10">
          <a
            href={d.primaryCTA.href}
            data-testid="systems-cta-primary"
            className="group inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 text-base font-semibold text-[#0b0f19] shadow-md transition-all hover:gap-3 hover:scale-[1.02] w-full sm:w-auto"
            style={{ background: 'linear-gradient(90deg, #e6c35a 0%, #c9a227 50%, #a8871a 100%)' }}>
            <span>{ts.cta_primary}</span>
            <ArrowRight className="w-5 h-5" />
          </a>
          <a
            href={d.secondaryCTA.href}
            className="inline-flex items-center justify-center font-semibold px-8 py-3.5 rounded-full text-base transition-colors border bg-white border-gray-200 text-[#1a1a1a] hover:bg-stone-50 w-full sm:w-auto">
            {ts.cta_secondary}
          </a>
        </div>

        {/* Direct line — same pattern Academy uses for callers who want to skip forms */}
        <div
          className="mx-auto max-w-[480px] rounded-3xl bg-white/70 border border-stone-200/80 backdrop-blur-md p-5 md:p-6"
          data-testid="systems-talk-panel"
        >
          <span className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-zinc-800">Direct line</span>
          <a
            href="tel:+19142000609"
            data-testid="systems-cta-phone"
            className="mt-1.5 inline-flex items-center gap-2 text-2xl md:text-3xl font-extrabold text-[#0e0e10] hover:text-zinc-800 transition-colors"
          >
            <Phone className="w-5 h-5 md:w-6 md:h-6 text-[#C9A227]" />
            <span className="font-mono tracking-wider">914-200-0609</span>
          </a>
          <div className="text-xs text-[#6E6E73] mt-1">Mon–Fri · 9 am – 6 pm ET · Same-day quotes</div>
        </div>
      </div>
    </section>
  );
}

export default function Systems() {
  const { t, lang } = useI18n();
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const seo = t?.seo?.systems || {};
  const faqItems = (t?.systems?.cards?.faq_items) || [];
  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'ZHELTA Systems',
      serviceType: 'Managed IT Services, Cybersecurity, Cloud Infrastructure',
      provider: { '@id': `${origin}/#organization` },
      areaServed: { '@type': 'Place', name: 'Worldwide' },
      description: seo.description || 'Professional IT management and cybersecurity protection for modern businesses.',
      url: `${origin}/systems`,
      image: seo.og_image || `${origin}/og-images/systems.svg`,
      inLanguage: lang,
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Comprehensive IT solutions',
        itemListElement: [
          'Managed IT Services', 'IT Systems Management', 'Cybersecurity Solutions',
          'Cloud Infrastructure', 'Help Desk Support', 'Network Security',
        ].map((n) => ({
          '@type': 'Offer',
          itemOffered: { '@type': 'Service', name: n },
        })),
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
        { '@type': 'ListItem', position: 2, name: 'ZHELTA Systems', item: `${origin}/systems` },
      ],
    },
    ...(faqItems.length > 0 ? [{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      inLanguage: lang,
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <SeoHead
        pageKey="systems"
        path="/systems"
        title="ZHELTA Systems — Enterprise IT Solutions, Simplified"
        description="Professional IT management and cybersecurity protection that keeps your business running smoothly, securely, and efficiently."
      />
      <StructuredData schemas={schemas} />
      <SystemsHeader />
      <DivisionStripBar current="systems" />
      <main>
        <Hero />
        <SystemsPartnersStrip />
        <Why />
        <Solutions />
        <Process />
        <Compare />
        <FAQSection
          eyebrow={t?.systems?.faq_eyebrow}
          title={t?.systems?.faq_title}
          subtitle={t?.systems?.faq_subtitle}
          items={faqItems}
          accent="text-[#C9A227]"
          testIdPrefix="systems-faq"
        />
        <FinalCTA />
      </main>
      <RecommendedForYou division="systems" heading="To get more out of Systems" />
      <CrossDivisionStrip hide={['systems']} />
      <AppearancePreFooter />
      <SystemsFooter />
    </div>
  );
}
